/**
 * 업로드 직전에 사진을 "서버가 어차피 남길 크기"로 줄인다.
 *
 * 왜 필요한가
 * ----------
 * 리뷰는 사진을 JSON 본문에 base64로 담아 보낸다. 그런데 줄이지 않고 보냈다.
 * 폰 원본은 가로 4000px가 넘고, base64는 여기에 1.33배를 더 붙인다.
 * 사진 한 장이 약 5MB, 다섯 장이면 요청 하나가 24MB였다.
 *
 * 서버는 그걸 받자마자 가로 1440px로 줄여서 저장한다(ImageOptimizer).
 * 즉 올린 바이트의 약 90%는 받아서 버리는 화소였다.
 *
 * 실제로 터졌다
 * ------------
 * 2026-08-31 새벽, 사진 다섯 장을 넣은 사용자가 LTE에서 저장에 여섯 번 실패했다.
 * nginx 로그에 400 다섯 번과 408 한 번이 남았고, Laravel은 그 요청을 아예
 * 받지 못했다 — 본문이 다 도착하기 전에 시간이 다 됐다는 뜻이다.
 * axios 타임아웃 60초, nginx client_body_timeout 기본 60초. 24MB를 새벽
 * LTE로 밀어 올리기엔 모자랐다. 회선이 바뀌자 같은 사진이 그대로 저장됐다.
 *
 * 사진을 많이 넣을수록 실패한다는 건 제일 나쁜 종류의 버그다. 사진을 덜 넣게 만든다.
 *
 * 화질은 안 깎인다
 * ---------------
 * 서버가 만들던 것과 같은 크기·같은 품질로 만들어 보낸다(가로 1440 / JPEG 82).
 * 지금 저장돼 있는 사진과 사실상 같은 결과물이고, 옮기는 바이트만 약 90% 준다.
 */

/** 서버 ImageOptimizer::MAX_WIDTH 와 같은 값. 다르면 화질이 손해다. */
const MAX_WIDTH = 1440

/** 서버 ImageOptimizer::QUALITY(82) 와 같은 값 */
const QUALITY = 0.82

/**
 * 투명도가 있는 형식은 JPEG로 바꾸면 배경이 검게 변한다.
 * 서버도 같은 이유로 이 형식들은 건드리지 않는다.
 */
const KEEP_AS_IS = /^data:image\/(png|gif|webp)/i

/** data URL이 실제 내용을 담고 있는지 (빈 'data:,' 방어) */
function isNonEmptyImageDataUrl(dataUrl: string): boolean {
  return (
    dataUrl.startsWith('data:image/') &&
    dataUrl.length > 'data:image/jpeg;base64,'.length
  )
}

/**
 * 사진 하나를 업로드용 크기로 줄인다.
 *
 * 어떤 이유로든 줄이지 못하면 **받은 것을 그대로 돌려준다**.
 * 사진을 잃는 것보다 크게 올라가는 편이 낫다.
 *
 * @param dataUrl 원본 미리보기 (data URL). 서버 URL이면 그대로 돌려준다.
 */
export async function downscaleForUpload(dataUrl: string): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) return dataUrl
  if (KEEP_AS_IS.test(dataUrl)) return dataUrl

  let image: HTMLImageElement
  try {
    image = await loadImage(dataUrl)
  } catch {
    return dataUrl
  }

  // 브라우저는 EXIF 회전을 적용한 뒤의 크기를 준다. 그래서 캔버스에 그리면
  // 이미 똑바로 선 픽셀이고, 회전 태그 없이 저장해도 눕지 않는다.
  const width = image.naturalWidth
  const height = image.naturalHeight
  if (!width || !height) return dataUrl

  // 이미 서버가 남길 크기다 — 다시 그려봐야 화질만 한 번 더 깎인다
  if (width <= MAX_WIDTH) return dataUrl

  const outW = MAX_WIDTH
  const outH = Math.max(1, Math.round((height * MAX_WIDTH) / width))

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH

  const ctx = canvas.getContext('2d')
  if (!ctx) return dataUrl
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(image, 0, 0, outW, outH)

  let out: string
  try {
    out = canvas.toDataURL('image/jpeg', QUALITY)
  } catch {
    return dataUrl
  }
  if (!isNonEmptyImageDataUrl(out)) return dataUrl

  // 줄지 않았으면 원본이 낫다 (이미 잘 압축된 작은 사진)
  if (out.length >= dataUrl.length) return dataUrl

  return out
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('image load failed'))
    image.src = src
  })
}
